import SyncDirection from '../../models/playlist/syncDirection';

export interface SyncDiffInput {
  /**
   * Remote ids as of the last sync. Undefined on the first sync, which then
   * behaves as a union: everything on either side is new to the other.
   */
  base: string[] | undefined;
  /** Ids currently in the remote playlist. */
  remote: string[];
  /** Ids currently in the Fadila playlist, for this source's service. */
  local: string[];
  direction: SyncDirection;
  /** Whether we hold a token that may write to the remote at all. */
  canPush: boolean;
}

export interface SyncDiff {
  /** Added at the remote since the last sync; to be added to the Fadila playlist. */
  pullAdds: string[];
  /** Removed at the remote since the last sync; to be removed from the Fadila playlist. */
  pullRemoves: string[];
  /** Added in Fadila since the last sync; to be written to the remote playlist. */
  pushAdds: string[];
  /** Removed in Fadila since the last sync; to be removed from the remote playlist. */
  pushRemoves: string[];
}

function unique(ids: string[]) {
  return Array.from(new Set(ids));
}

/**
 * Three-way set diff between what the remote has, what Fadila has, and what
 * both had when they were last in step.
 *
 * Written for YouTube and used unchanged for SoundCloud: nothing here knows
 * which service it is comparing, only ids. What differs between providers is
 * how the four change sets are applied - YouTube inserts and deletes one
 * item at a time, SoundCloud replaces a playlist's whole track list.
 *
 * Only membership is compared. Ordering is not synced, and duplicates are
 * collapsed - a track twice in a remote playlist is one id here.
 *
 * Each of the four change sets is decided by where an id sits relative to the
 * base snapshot:
 *
 *   in remote, not in base, not in local  -> appeared remotely  -> pull add
 *   in base, not in remote, still in local -> vanished remotely  -> pull remove
 *   in local, not in base, not in remote  -> added in Fadila    -> push add
 *   in base, not in local, still in remote -> removed in Fadila  -> push remove
 *
 * An id both sides already agree on needs nothing, and one that both sides
 * dropped independently needs nothing either. The direction then decides which
 * of the four sets are acted on; the ones that are not stay pending, because
 * the base is only advanced to what the remote actually holds afterwards.
 */
export function diffSync({ base, remote, local, direction, canPush }: SyncDiffInput): SyncDiff {
  const baseSet = new Set(base || []);
  const remoteSet = new Set(remote);
  const localSet = new Set(local);

  const pull = direction !== SyncDirection.ToSource;
  const push = canPush && direction !== SyncDirection.FromSource;

  const diff: SyncDiff = {
    pullAdds: [],
    pullRemoves: [],
    pushAdds: [],
    pushRemoves: [],
  };

  if (pull) {
    diff.pullAdds = unique(remote).filter((id) => !baseSet.has(id) && !localSet.has(id));
    diff.pullRemoves = unique(local).filter((id) => baseSet.has(id) && !remoteSet.has(id));
  }

  if (push) {
    diff.pushAdds = unique(local).filter((id) => !baseSet.has(id) && !remoteSet.has(id));
    diff.pushRemoves = unique(remote).filter((id) => baseSet.has(id) && !localSet.has(id));
  }

  return diff;
}
